import type { ThunkDispatch, AnyAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import http from "@/utils/request";
import { EMPTY_INCOME_TYPES, ACCOUNT_TYPES_CONFIG_KEY, BUSINESS_TYPES_CONFIG_KEY, parseIncomeTypeOptions } from "@/utils/incomeTypes";
import type { IncomeTypeConfig } from "@/utils/incomeTypes";

interface IncomeTypesState {
  config: IncomeTypeConfig;
  status: "idle" | "loading" | "ready" | "failed";
  error: string | null;
  requestId?: string;
}
interface IncomeTypesRoot { incomeTypes: IncomeTypesState }
export type IncomeTypesDispatch = ThunkDispatch<IncomeTypesRoot, unknown, AnyAction>;
const initialState: IncomeTypesState = { config: EMPTY_INCOME_TYPES, status: "idle", error: null };

export const loadIncomeTypes = createAsyncThunk<IncomeTypeConfig, boolean | undefined, { state: IncomeTypesRoot }>(
  "incomeTypes/load",
  async () => {
    const [accountResponse, businessResponse] = await Promise.all([
      http.get<never, { msg?: string; data?: string | null }>(`/system/config/configKey/${ACCOUNT_TYPES_CONFIG_KEY}`),
      http.get<never, { msg?: string; data?: string | null }>(`/system/config/configKey/${BUSINESS_TYPES_CONFIG_KEY}`),
    ]);
    // 现有参数查询接口通过 msg 返回字符串配置值。
    return {
      accountTypes: parseIncomeTypeOptions(accountResponse.data ?? accountResponse.msg ?? null, "accountTypes"),
      businessTypes: parseIncomeTypeOptions(businessResponse.data ?? businessResponse.msg ?? null, "businessTypes"),
    };
  },
  { condition: (force, { getState }) => force || ["idle", "failed"].includes(getState().incomeTypes.status) },
);

const slice = createSlice({
  name: "incomeTypes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(loadIncomeTypes.pending, (state, action) => { state.status = "loading"; state.requestId = action.meta.requestId; });
    builder.addCase(loadIncomeTypes.fulfilled, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.config = action.payload; state.status = "ready"; state.error = null;
    });
    builder.addCase(loadIncomeTypes.rejected, (state, action) => {
      if (state.requestId !== action.meta.requestId) return;
      state.status = "failed"; state.error = action.error.message ?? "收益类型配置加载失败";
    });
  },
});

export function useIncomeTypes(): IncomeTypeConfig {
  const dispatch = useDispatch<IncomeTypesDispatch>();
  const config = useSelector((state: IncomeTypesRoot) => state.incomeTypes.config);
  useEffect(() => { void dispatch(loadIncomeTypes()); }, [dispatch]);
  return config;
}

export default slice.reducer;
